import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@jornada/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * **ZERO rows and return "success"**. Not a hypothesis: measured in
 * `space-academy-app` (same canon, local stack `activity` table, course
 * `06a131d0`) — course is created, 1.03 s later unit is created and **in
 * between there is NO `course/updated` op**: the title the author had just
 * written never generated a CRUD operation. In a run where
 * everything queued before the first upload (`136ef638`) it is present, 7 ms from
 * `created`.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the local
 * view from the last checkpoint**. If the checkpoint does not yet bring the
 * newly created row, the row is **ABSENT during that instant** — and an
 * UPDATE against it writes nothing, generates no op, and **does not fail**. This repo already
 * assumed that transient absence exists: it is the raison d'etre of
 * `apps/web/src/lib/use-entity-resolution.ts` ("row may not have
 * synchronized yet").
 *
 * Why the absence of an error proves absolutely nothing here: the server
 * write-path returns **2xx always on purpose** (a 4xx would block the client
 * of client queue forever, see `infra/PENDING_WORK.md` §1.5.7 and
 * `lanzamiento-nativo-t0-t6/T6-auditoria-descartes.md`). That case is a DIFFERENT
 * DIFFERENT — there the server rejects and silences —, but criterion is the same:
 * a user write that disappears without trace is the worst failure mode.
 *
 * Contract of this module: write, **REREAD**, compare. If the value did not
 * land it retries with backoff; if upon exhaustion it still has not landed, it
 * **THROWS** — caller communicates it instead of leaving user staring at a
 * field that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberate) ───────────────────────────
 * Los `DELETE` duros (`purgeTodoMutation`, `purgeListMutation`,
 * `deleteListGroupOptimisticMutation`, `deleteSubtaskMutation`) are not verified:
 * its natural check would be "row is no longer there", and "absent" is the
 * ambiguous state in this window — cannot distinguish "deleted" from "not yet
 * had arrived". Verifying them would require another signal (CRUD queue itself), not
 * a reread. Signal that reopens: a purge genuinely lost
 * (row reappearing in trash after purge).
 *
 * ── `scope`: tenant isolation, not just correctness ─────────────────────────
 * Brought from `comandero-app` (`f8f1049`), where removing the
 * `organization_id` condition from `WHERE` when migrating to `patchRowVerified` **opened
 * cross-tenant writes between organizations**: `infra/sync_streams.yaml`
 * syncs ALL organizations where the user is a `member` (not
 * just active — YAML itself says "client scopes"), and row `id`
 * travels in URL. Without extra filter, an `UPDATE … WHERE id = ?` launched
 * from organization A context landed equally on a row of organization
 * B if `id` matched — tenant `WHERE` was not
 * redundant with PK, it was isolation in depth. `scope` restores that
 * filter **applied to both UPDATE and reread**: if applied only
 * to one, verification would lie (rereads without filter and confirms
 * a write that actually landed outside scope, or vice versa). A
 * row not matching scope is treated same as absent row: write is
 * never confirmed and function terminates throwing
 * `LocalWriteLostError` — same mechanism as checkpoint window, but
 * for right reason (not yours, not arrived late). `listto-app`
 * is not multi-org today, so no mutation here uses it
 * yet; lives here so multi-org clones (`field-check`,
 * `stockpilot`, `jornada`, `custodia`...) inherit it mechanically
 * instead of having to reinvent it under pressure.
 *
 * ── THREE pieces on `updated_at`, and they are THREE different things ──────
 * They overlap in column name but not in behavior — each
 * has its own flag, and none implies the others:
 *
 *   1. **Stamping** (`hasUpdatedAt`, default `true`): whether this helper
 *      writes `updated_at` in the `SET` of every attempt.
 *   2. **Ignore any `updated_at` supplied by the `patch`** (always, with no flag):
 *      it is never compared or passed through to SET unchanged.
 *   3. **Verify BY `updated_at`** (`verifyBy: "updated_at"`, opt-in): use
 *      the stamped timestamp as proof of landing instead of comparing the
 *      patch by value.
 *
 * ── 1. `hasUpdatedAt: false` — tables lacking that column ────────────────────
 * Brought from `vigia-fleet-app` (`6766c06`): `device_alert` and
 * `remote_control_session` lack `updated_at` column, and canon
 * stamped unconditionally — would have crashed at runtime
 * against real SQLite (non-existent column), a failure that test doubles with
 * mock tables do NOT catch because they lack real schema behind them. With
 * `hasUpdatedAt: false` the `SET` does not touch that column at all, and
 * `verifyBy: "updated_at"` becomes invalid (throws on entry, before writing
 * anything, instead of failing against SQLite midway). **Prior check
 * for whoever converts a repo: do all tables you protect have
 * `updated_at`? If not, pass it explicitly — do not assume from rest of repo.**
 *
 * ── 2. Ignore `updated_at` incoming in `patch` itself ────────────────────────
 * Brought from `agape-church-app`: multiple mutations reuse a single `values`
 * object between their INSERT and UPDATE branches, and that object already carries
 * its own `updated_at` for INSERT. If leaked without stripping, it would be
 * compared as domain field against FRESH `updated_at` stamped by this
 * helper on each attempt — which never matches — and patch would fail
 * ALWAYS for the wrong reason (not because write did not land, but
 * because imported `updated_at` was already stale before
 * writing). Ignored unconditionally, not just when it bites: not a
 * domain field, controlled by this module. Independent of `hasUpdatedAt`:
 * ignored equally even if table lacks column (a patch bringing it
 * there would be caller error, not something to slip into SET).
 *
 * ── 3. `verifyBy: "updated_at"` — EXPLICIT mode, never automatic ────────────
 * Also from `agape-church-app`: `bumpHeadcount` (attendance) uses atomic
 * atomic `sql\`MAX(0, COALESCE(headcount, 0) + ?)\`` in patch — read-add-
 * write in JS would reopen double-tap race condition that SQL avoids on
 * purpose. A SQL expression has no known expected value in
 * in advance: cannot compare by value upon reread. The first
 * draft of `agape` solved this with automatic *fallback* (if no literal
 * field remains in patch, compare `updated_at` instead); deliberately
 * discarded: silent fallback turns "cannot compare" into
 * "accept as good", masking cases where patch should NOT
 * being comparable by value (caller error, mistyped field). With
 * `verifyBy: "updated_at"` caller explicitly declares "verify that
 * THIS write landed, not that value matches expected" — if omitted and
 * patch brings SQL expression, value comparison can never match and
 * function terminates throwing `LocalWriteLostError` even if write
 * landed: **fail-closed**, not a false green.
 *
 * ── Why NO `patchRowsVerified` / `waitForRowsPresent` (batched) ────────────
 * `folio-office-app` (`1a7b5b3`) and `apunte-digital-app` (`4cc1a014`) added
 * them to move a page and its cohort of subpages at once
 * (`WHERE id IN (...)`, lands only when ALL batch rows reflect
 * patch). `listto-app` has no mutation touching more than one row
 * per write — `purgeListMutation`/`restoreListMutation`/
 * `restoreListGroupItemMutation` operate row by row, without `inArray` in any
 * `packages/shared/src/**\/mutations.ts` (verified before deciding this, not
 * assumed) — so bringing batch primitive here would be dead code without
 * caller: kind of debt that bit in `lib/gestures`, which diverged in
 * 4 forms when copied to repos that did not need it. Signal that reopens
 * this: a mutation in `listto` moving more than one row with the
 * same timestamp (e.g. trash by cohort of lists+todos).
 *
 * ── Second class: upsert deciding INSERT-vs-UPDATE with own SELECT ───────────
 *
 * `patchRowVerified`/`waitForRowPresent` protect write against a row
 * whose `id` is ALREADY known. A different class neither covers:
 * an upsert deciding whether to INSERT or UPDATE with own SELECT, because there is
 * no `id` to reread until that branch is decided. That SELECT falls into SAME
 * checkpoint window: if row exists but just reappeared after a
 * rebuild, SELECT sees it absent and code INSERTs a new row — data is not
 * lost, row is DUPLICATED, without any visible error.
 *
 * Caracterizada en cinco clones: `folio-office-app` (hilos de comentarios),
 * `habit-streak-app` (registro diario), `boveda-vault-app`, `vigia-fleet-app`
 * (device onboarding — duplication **proven** by reverting and re-running) and
 * `meal-planner-app` (pantry aisles and weekly list). Solved in
 * `stockpilot-app` (`b8c4bc8a`) with `readMatchingRowVerified`, for its hard case:
 * `stock_quant` decides by CONTENT key (product×location×lot),
 * without an `id` the caller can know in advance.
 *
 * `readMatchingRowVerified` lives here in canon, but **`listto-app` does not
 * need it today**: no mutation in this repo decides INSERT-vs-UPDATE with a
 * dedicated SELECT (verified, not assumed — all `create*Mutation` in
 * `packages/shared/src/**\/mutations.ts` generate their `id` with `generateId()` and
 * INSERT directly; neither `onConflictDoUpdate` nor SELECT followed by
 * insert/update branch against PowerSync view). It is **hosted** so
 * that the five clones above (and any other adopting this form)
 * inherit it from mechanical conversion instead of reinventing under pressure —
 * just as happened with `scope` before `listto` was multi-org. **Do not
 * delete as "unused code"**: no caller in THIS repo on purpose,
 * has callers in five. Signal that reopens this: a `listto` mutation that
 * starts deciding INSERT-vs-UPDATE by content instead of known `id`.
 *
 * Two ways to close this class, not interchangeable — full guide
 * (with criteria for choosing) in
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md`:
 *
 *   1. **Explicit `isNew` from caller.** If caller already
 *      knows whether creating or updating (comes from distinct "New" button
 *      vs "Save", or distinct `/new` route vs
 *      `/:id/edit`), pass as parameter and skip decision SELECT
 *      completely. Simpler and zero-cost — closed this way in `folio-office-app` and
 *      `vigia-fleet-app`. Valid when caller knows; not valid
 *      when key is content-based and nobody knows in advance.
 *   2. **`readMatchingRowVerified`.** When row is identified by
 *      CONTENT (composite key, not `id` brought by caller) and thus
 *      no one knows in advance if it exists. Retries decision read
 *      with same backoff as rest of module, BEFORE caller
 *      concludes "does not exist yet, must create".

/**
 * Retry backoff. Sums ≈ 6.05 s: more than covers the round trip of a
 * checkpoint (measured at ~1 s against the local stack) without leaving a fast
 * fast interaction — mark important, complete — hanging forever if the
 * not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition — tenant isolation in depth. */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Delays between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
  /**
   * Extra conditions row MUST ALSO satisfy, in addition to `id` —
   * typically `organization_id`. They apply to both the UPDATE and the
   * reread: a row that does not satisfy the scope is treated like a
   * row (see security note above).
   */
  scope?: readonly ScopeCondition[];
  /**
   * Whether table has `updated_at` column. Default `true` (standard
   * behavior: stamped on each attempt). Set to `false`
   * on tables that LACK that column (see note above, piece 1) — with
   * `false`, `patchRowVerified` never inserts it into `SET`, and `verifyBy:
   * "updated_at"` ceases to be valid (no column to compare).
   */
  hasUpdatedAt?: boolean;
  /**
   * How to verify write landed, when patch cannot be compared
   * by value (e.g. atomic increment SQL expression).
   * EXPLICIT mode, never automatic — see note above, piece 3. `"updated_at"`
   * compares seal stamped by this helper on each attempt: proves THIS
   * write landed, not that final value matches expected. Requires
   * `hasUpdatedAt` not `false`.
   */
  verifyBy?: "updated_at";
}

/** Error for a local-first write that failed to land in view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Builds `eq(id) AND eq(scope1) AND …` — or just `eq(id)` without scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Reads row by id (+ `scope` if given), or `undefined` if not in
 * local view or fails scope — same result as "absent row", on
 * purpose (see security note above).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer shape of generic `SQLiteTable`: select
  // resolves to `{ [x: string]: any }[]`, so annotating destination suffices — no
  // need to pass through `unknown`, which would narrow without real check.
  const rows: Record<string, unknown>[] = await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1);
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when comparing against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, rereads, compares the patch
 * fields and retries as long as they have not landed. Throws `LocalWriteLostError`
 * if they are still missing upon exhausting the backoff.
 *
 * If `options.hasUpdatedAt` is not `false` (standard case), `updated_at` is
 * set by this function on each attempt — any `updated_at` in
 * `patch` itself is ALWAYS ignored, both writing and comparing (see
 * note above, piece 2); remainder of `patch` must carry only domain
 * fields. With `hasUpdatedAt: false` (tables lacking column, see piece 1)
 * `SET` does not touch it at all. With `options.verifyBy === "updated_at"` patch
 * is not compared by value: verifies seal of THIS attempt
 * landed (see note above, piece 3) — combining with `hasUpdatedAt: false`
 * is configuration error and throws immediately, before writing anything.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];
  const hasUpdatedAt = options.hasUpdatedAt ?? true;
  if (options.verifyBy === "updated_at" && !hasUpdatedAt) {
    throw new Error(
      'verifyBy: "updated_at" is incompatible with hasUpdatedAt: false — the table does not have that column to compare.',
    );
  }
  const { updated_at: _ignoredUpdatedAt, ...domainPatch } = patch;

  for (let attempt = 0; ; attempt++) {
    const stampedAt = hasUpdatedAt ? nowISO() : undefined;
    await db
      .update(table)
      .set(hasUpdatedAt ? { ...domainPatch, updated_at: stampedAt } : domainPatch)
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    const landed =
      row !== undefined &&
      (options.verifyBy === "updated_at"
        ? sameValue(row.updated_at, stampedAt)
        : Object.entries(domainPatch).every(([key, value]) => sameValue(row[key], value)));
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(domainPatch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for row to EXIST in local view before mutating (+ `scope`
 * if given, with same isolation criteria as `patchRowVerified`).
 *
 * For mutations that are not a simple patch — a transaction completing
 * a recurring task and spawning the next, or an `order` swap
 * between two subtasks. Retrying inside transaction is invalid (would
 * sleep with write lock held), and damage of operating on missing row
 * is worse than losing a field: UPDATE matches nothing but companion
 * INSERT occurs, so original task never completes **and** phantom appears
 * su sucesora duplicada.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id, scope)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Rereads, with same backoff as rest of module, a row
 * identified by CONTENT rather than `id` — case of `stock_quant` in
 * `stockpilot-app` (product×location×lot key), or comment thread /
 * daily log / device enrollment deciding INSERT-vs-UPDATE
 * inspecting its own content columns. See "Second class" note
 * above: `listto-app` has NO caller for this today — lives here
 * HOSTED for clones that do need it.
 *
 * ALWAYS called before opening transaction deciding INSERT-vs-UPDATE, and
 * NEVER inside it — same reason as `waitForRowPresent`: retrying with
 * write lock already held sleeps entire transaction, not just
 * read. When multiple rows must be resolved (multiple order lines,
 * for example), resolve in PARALLEL (`Promise.all`) before
 * transaction: genuinely new line does not pay latency of
 * backoff from another line that needed retries.
 *
 * Unlike `patchRowVerified`/`waitForRowPresent`, exhausting backoff
 * is NOT an error here: genuine absence (first time touching that
 * content combination) is VALID result, so this function
 * returns `undefined` instead of throwing, and caller proceeds to INSERT
 * exactly as if it had not retried. Backoff only narrows
 * false-negative window; cannot eliminate completely without another signal (the
 * CRUD queue itself).
 *
 * `read` must repeat SAME query on each attempt — a function that
 * reconstructs `where` over `db`/`trx`, not already resolved promise
 * reused without touching DB again.
 */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}
